import { captureException } from '@/utils/sentry';
import { getCodeFromGQLError } from '@/utils/apollo-client';
import { gql, useMutation } from '@apollo/client';
import {
  OrderSummary_ApplyPromotionsMutation,
  OrderSummary_ApplyPromotionsMutationVariables,
} from '@/types';
import { useCallback } from 'react';
import { useToasts } from '@/contexts/toast-context';

export const APPLY_PROMOTIONS_MUTATION = gql`
  mutation OrderSummary_ApplyPromotions(
    $discountCodes: [String!]!
    $orderUUID: uuid!
  ) {
    applyPromotions(order_id: $orderUUID, discount_codes: $discountCodes) {
      order_id
    }
  }
`;

interface ApplyPromotionsArgs {
  discountCodes: string[];
  draftOrderUUID: string;
}

export interface ApplyPromotionsResult {
  errorCode: string | null;
  success: boolean;
}

export const useApplyPromotions = (): [
  (args: ApplyPromotionsArgs) => Promise<ApplyPromotionsResult>,
  { loading: boolean },
] => {
  const { showErrorToast } = useToasts();
  const [mutate, { loading }] = useMutation<
    OrderSummary_ApplyPromotionsMutation,
    OrderSummary_ApplyPromotionsMutationVariables
  >(APPLY_PROMOTIONS_MUTATION);

  const applyPromotions = useCallback(
    async ({
      discountCodes,
      draftOrderUUID,
    }: ApplyPromotionsArgs): Promise<ApplyPromotionsResult> => {
      try {
        const result = await mutate({
          variables: { discountCodes, orderUUID: draftOrderUUID },
        });
        return {
          errorCode: null,
          success: Boolean(result.data?.applyPromotions.order_id),
        };
      } catch (e) {
        const errorCode = getCodeFromGQLError(e);
        if (errorCode) {
          return { errorCode, success: false };
        }
        captureException(e);
        showErrorToast();
        return { errorCode: null, success: false };
      }
    },
    [mutate, showErrorToast],
  );

  return [applyPromotions, { loading }];
};
